import { useAuthStore } from '@/stores/authStore'
import { hasPlanFeature, type PlanFeatureKey } from '@/lib/planFeatures'
import type { GatedNode } from '@/lib/api'

/**
 * Client-side read of which plan features this account unlocks, fed by
 * `authStore.planStatus.features` (fetched once at login/boot from
 * `GET /auth/plan-status` -- every role, unlike `billingStore.subscription`
 * which 403s for Manager/Rep/Analyst).
 *
 * Powers the in-app locks: gated workflow nodes, out-of-tier enrichment
 * providers, plan-gated Settings sections. UX only -- the boundary is the
 * backend, which re-checks every one of these (plan_gating.py).
 *
 * Degrades OPEN, not closed: before `planStatus` has loaded, `features` is
 * null and `isLoaded` is false, and callers treat that as "not locked yet".
 * Guessing "locked" during the first paint would flash a lock on a feature
 * the account actually pays for, and the API refuses the action anyway.
 *
 * @example
 *   const { has } = usePlanFeatures()
 *   if (!has('ab_testing')) return <LockedNode />
 */
export interface PlanFeaturesState {
  /** Whether `plan-status` has answered yet. */
  isLoaded: boolean
  /** Raw feature keys, or null before the first load. */
  features: string[] | null
  /** True when the feature is unlocked *or* nothing has loaded yet. */
  has: (key: PlanFeatureKey) => boolean
  /** True only once loaded AND the feature is absent -- the "show a lock" test. */
  isLocked: (key: PlanFeatureKey) => boolean
  /** Workflow node types this plan may not use, as sent by the API. */
  gatedNodes: GatedNode[]
  /**
   * The gate on a workflow node type, or null when the node is fully
   * available. `partial: true` means only some of its options are gated --
   * the palette must still offer the node (see the backend's
   * PARTIALLY_GATED_NODE_TYPES).
   */
  gateForNode: (nodeType: string) => GatedNode | null
}

export function usePlanFeatures(): PlanFeaturesState {
  const planStatus = useAuthStore((s) => s.planStatus)
  const features = planStatus?.features ?? null
  const isLoaded = features !== null
  const gatedNodes = planStatus?.gated_nodes ?? []
  return {
    isLoaded,
    features,
    has: (key) => (isLoaded ? hasPlanFeature(features, key) : true),
    isLocked: (key) => isLoaded && !hasPlanFeature(features, key),
    gatedNodes,
    gateForNode: (nodeType) => gatedNodes.find((n) => n.type === nodeType) ?? null,
  }
}
